import { GERMAN_TIME_ZONE } from './dateTimeUtils';
const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * scheduledAt wird im Projekt als feste CET-Wandzeit behandelt.
 * Daher für die Anzeige immer +01:00 (ohne Sommerzeit-Sprung) anwenden.
 */
const normalizeScheduledAtForDisplay = (scheduledAt: string): Date => {
  const dateObj = new Date(scheduledAt);
  if (Number.isNaN(dateObj.getTime())) return dateObj;
  return new Date(dateObj.getTime() + ONE_HOUR_MS);
};

/**
 * Formatiert ein scheduledAt-Datum in deutscher Zeit (MEZ/MESZ)
 * scheduledAt ist in UTC gespeichert, muss zu deutscher Zeit konvertiert werden
 * 
 * @param scheduledAt ISO-String aus der Datenbank (in UTC gespeichert)
 * @returns Formatiertes Datum: "DD. Monat YYYY um HH:MM Uhr" (in deutscher Zeit)
 */
export const formatScheduledAtGerman = (scheduledAt: string): string => {
  // Parse das UTC-Datum und konvertiere zu deutscher Zeit (UTC+1 bzw. UTC+2 im Sommer)
  const dateObj = normalizeScheduledAtForDisplay(scheduledAt);
  
  // Nutze toLocaleString mit expliziter Europe/Berlin Zeitzone
  const germanTimeString = dateObj.toLocaleString('de-DE', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Format ist: "DD. Monat YYYY, HH:MM"
  // Ändere zu: "DD. Monat YYYY um HH:MM Uhr"
  return germanTimeString.replace(',', ' um') + ' Uhr';
};

/**
 * Formatiert ein scheduledAt-Datum in Kurzform (DD.MM.YYYY)
 * scheduledAt ist in UTC gespeichert, wird zu deutscher Zeit konvertiert
 * 
 * @param scheduledAt ISO-String aus der Datenbank (in UTC gespeichert)
 * @returns Formatiertes Datum: "DD.MM.YYYY" (in deutscher Zeit)
 */
export const formatScheduledDateShort = (scheduledAt: string): string => {
  const dateObj = normalizeScheduledAtForDisplay(scheduledAt);
  
  // Konvertiere zu deutscher Zeit
  const germanDateString = dateObj.toLocaleDateString('de-DE', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  return germanDateString;
};

/**
 * Formatiert ein scheduledAt-Datum mit Wochentag in deutscher Zeit
 * scheduledAt ist in UTC gespeichert, wird zu deutscher Zeit konvertiert
 * 
 * @param scheduledAt ISO-String aus der Datenbank (in UTC gespeichert)
 * @returns Formatiertes Datum: "Wochentag, DD.MM.YYYY um HH:MM Uhr" (in deutscher Zeit)
 */
export const formatScheduledAtWithWeekday = (scheduledAt: string): string => {
  const dateObj = normalizeScheduledAtForDisplay(scheduledAt);
  
  // Konvertiere zu deutscher Zeit mit Wochentag
  const germanTimeString = dateObj.toLocaleString('de-DE', {
    timeZone: 'UTC',
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Format ist: "Wochentag, DD.MM.YYYY, HH:MM"
  // Ändere zu: "Wochentag, DD.MM.YYYY um HH:MM Uhr"
  return germanTimeString.replace(/,([^,]*)$/, ' um$1 Uhr');
};

type DateInput = Date | string | number | null | undefined;

const toValidDate = (input: DateInput): Date | null => {
  if (!input) return null;
  const dateObj = input instanceof Date ? input : new Date(input);
  return Number.isNaN(dateObj.getTime()) ? null : dateObj;
};

/**
 * Formatiert ein Datum in deutscher Kurzform (DD.MM.YYYY)
 */
export const formatDateGerman = (input: DateInput): string => {
  const dateObj = toValidDate(input);
  if (!dateObj) return '';

  return dateObj.toLocaleDateString('de-DE', {
    timeZone: GERMAN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formatiert Datum und Uhrzeit in deutscher Langform
 * Beispiel: "01. Januar 2026, 10:30"
 */
export const formatDateTimeGerman = (input: DateInput): string => {
  const dateObj = toValidDate(input);
  if (!dateObj) return '';

  return dateObj.toLocaleDateString('de-DE', {
    timeZone: GERMAN_TIME_ZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

